
import { Component, Vue, Watch } from "vue-property-decorator";
import { debounce } from "typescript-debounce-decorator";
import httpClient from "../../store/httpClient";
import moment from "moment";
import FileSaver from "file-saver";
import { OfferConfirmationDto, OfferPositionDto } from "../../dtos/offerConfirmationDto";
import { OfferSearchRequestDto } from "../../dtos/offerSearchRequestDto";
import { OfferState } from "@/dtos/offerState";

@Component
export default class OfferDashboard extends Vue {
	private searchTerm: string = "";
	private loading: boolean = false;
	private offers: OfferConfirmationDto[] = [];
	private loadBatchSize: number = 10;
	private hasMoreToLoad: boolean = true;
	private createOrderDialog: boolean = false;
	private creatingOrder: boolean = false;
	private offerToOrder: OfferConfirmationDto | null = null;

	private headers = [
		{ text: "Status", value: "status", align: "left", sortable: false },
		{ text: "Nur Druck", value: "justToPrint", align: "left", sortable: false },
		{ text: "KundenNr", value: "customerId", align: "left", sortable: false },
		{ text: "Name", value: "customerName", align: "left", sortable: false },
		{ text: "KalkulationsNr.", value: "calculationId", align: "left", sortable: false },
		{ text: "Angebotsdatum", value: "orderDate", align: "left", sortable: false },
		{ text: "Gültig bis", value: "validUntil", align: "left", sortable: false },
		{ text: "", value: "", align: "right", sortable: false },
	];

	private positionHeaders = [
		{ text: "Pos", value: "position", align: "left", sortable: false, width: "1px" },
		{
			text: "Blexon Id",
			value: "blexonArticleId",
			align: "left",
			sortable: false,
			width: "60px",
		},
		{ text: "Bezeichnung", value: "customerArticleName", align: "left", sortable: false },
		{ text: "Eigenschaften", value: "attributes", align: "left", sortable: false },
		{ text: "Menge", value: "quantity", align: "left", sortable: false, width: "1px" },
		{ text: "Preis/Stk", value: "pricePerUnit", align: "right", sortable: false, width: "1px" },
		{ text: "Total", value: "total", align: "right", sortable: false, width: "1px" },
	];

	beforeRouteEnter(to: any, from: any, next: any) {
		next((vm: any) => {
			window.addEventListener("scroll", vm.checkBottom);
		});
	}

	beforeRouteLeave(to: any, from: any, next: any) {
		window.removeEventListener("scroll", this.checkBottom);
		next();
	}

	private mounted() {
		this.loadOffers(true);
	}

	@debounce(700, { leading: false })
	@Watch("searchTerm")
	private onSearchTermChanged() {
		if (this.searchTerm.length === 0 || this.searchTerm.length >= 3) {
			this.loadOffers(true);
		}
	}

	private checkBottom() {
		const bottomOfWindow =
			window.innerHeight + window.pageYOffset >= document.body.offsetHeight - 2;
		if (bottomOfWindow && this.hasMoreToLoad && !this.loading) {
			this.loadOffers(false);
		}
	}

	private async loadOffers(reload: boolean) {
		if (this.loading) {
			return;
		}
		try {
			this.loading = true;
			const searchRequest = new OfferSearchRequestDto();
			searchRequest.from = reload ? 0 : this.offers.length;
			searchRequest.to = searchRequest.from + this.loadBatchSize;
			searchRequest.calculationId = parseInt(this.searchTerm) || 0;
			searchRequest.searchTerm = this.searchTerm;

			const response = await httpClient().post("Offer/GetOffers", searchRequest);
			if (reload) {
				this.offers = response.data;
			} else {
				response.data.forEach((newOffer: OfferConfirmationDto) => {
					const index = this.offers.findIndex(
						(o) => o.calculationId === newOffer.calculationId
					);
					if (index >= 0) {
						this.offers.splice(index, 1, newOffer);
					} else {
						this.offers.push(newOffer);
					}
				});
			}
			this.hasMoreToLoad = response.data.length === this.loadBatchSize;
		} catch {
			this.$store.commit("setSnackbarErrorText", "Fehler beim Laden der Angebote.");
		} finally {
			this.loading = false;
		}
	}

	private manuallyLoadMore() {
		if (this.hasMoreToLoad && !this.loading) {
			this.loadOffers(false);
		}
	}

	private customSort(items: any) {
		return items;
	}

	private getCustomerName(offer: OfferConfirmationDto): string {
		if (!offer.customerAddress) return "";
		const addresse = offer.customerAddress;
		return `${addresse.companyName}, ${addresse.givenName} ${addresse.surname}`.trim();
	}

	private isExpired(offer: OfferConfirmationDto): boolean {
		return moment(offer.validUntil).isBefore(moment(), "day");
	}

	private getValidityColor(offer: OfferConfirmationDto): string {
		if (offer.offerState == OfferState.Accepted) {
			return "green";
		}
		if (this.isExpired(offer)) {
			return "orange";
		}
		if (offer.offerState == OfferState.WaitingForCustomerAnswer) {
			return "grey";
		}
		if (offer.offerState == OfferState.Declined) {
			return "red";
		}
		return "blue";
	}

	private getValidityIcon(offer: OfferConfirmationDto): string {
		if (offer.offerState == OfferState.Accepted) {
			return "event_available";
		}
		return this.isExpired(offer) ? "event_busy" : "event_available";
	}

	private getValidityTitle(offer: OfferConfirmationDto): string {
		if (offer.offerState == OfferState.Accepted) {
			return "Angenommen";
		}
		if (this.isExpired(offer)) {
			return "Abgelaufen";
		}
		if (offer.offerState == OfferState.WaitingForCustomerAnswer) {
			return "Offen";
		}
		if (offer.offerState == OfferState.Declined) {
			return "Abgelehnt";
		}
		return "";
	}

	private getTotal(offer: OfferConfirmationDto): number {
		return offer.positions.reduce((sum, pos) => sum + pos.pricePerUnit * pos.quantity, 0);
	}

	private getFeatureString(position: OfferPositionDto): string {
		return position.features.map((f) => `${f.count} * ${f.name}`).join(" | ");
	}

	private goToCustomer(customerId: number) {
		this.$router.push({
			name: "customerDetail",
			params: { customerId: customerId.toString() },
		});
	}

	private openCreateOrderDialog(offer: OfferConfirmationDto) {
		this.offerToOrder = offer;
		this.createOrderDialog = true;
	}

	private async createOrder() {
		if (this.offerToOrder === null || this.creatingOrder) {
			return;
		}
		try {
			this.creatingOrder = true;
			await httpClient().post(
				`Offer/CreateOrder?calculationId=${this.offerToOrder.calculationId}`
			);
			this.$store.commit("setSnackbarText", "Die Bestellung wurde ausgelöst.");
			this.createOrderDialog = false;
			this.loadOffers(true);
		} catch {
			this.$store.commit("setSnackbarErrorText", "Fehler beim Auslösen der Bestellung.");
		} finally {
			this.creatingOrder = false;
		}
	}

	private async downloadOffer(offer: OfferConfirmationDto) {
		try {
			const response = await httpClient().get(
				`Offer/GetOfferAsPdf?calculationId=${offer.calculationId}`,
				{
					method: "GET",
					responseType: "blob",
				}
			);
			var filename = response.headers["content-disposition"].split('filename="')[1];
			if (filename == undefined) {
				filename = response.headers["content-disposition"].split("filename=")[1];
				filename = filename.split("; filename*=")[0];
			} else {
				filename = filename.split('"; filename*=')[0];
			}
			FileSaver.saveAs(new Blob([response.data]), filename);
		} catch (err) {
			console.error(`Die Pdf-Datei konnte nicht geladen werden. Error: ${err}`);
		}
	}
}
